import { Box, Button, Grid, MenuItem, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useQuery } from 'react-query';
import { Loader, SMBSelect } from '../../../components';
import { IDropdownResponse } from '../../../models';
import { createEmailTest, getEmailTypes } from '../../../fetch';
import { Email } from '@mui/icons-material';
import { alphaSort } from '../../../helpers';

const EmailTestSchema = Yup.object().shape({
  emailType: Yup.string().required('Required'),
  toAddress: Yup.string().email('Invalid email').required('Required')
});

export const EmailTestsForm = () => {
  const { enqueueSnackbar } = useSnackbar();

  const { isLoading: isLoadingEmailTypes, data: emailTypes } = useQuery<IDropdownResponse[], Error>(
    ['email-types'],
    () => getEmailTypes(),
    {
      onSuccess: d => alphaSort(d, 'description')
    }
  );

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        emailType: '',
        toAddress: ''
      }}
      validationSchema={EmailTestSchema}
      onSubmit={async (values, actions) => {
        try {
          await createEmailTest(values);
          enqueueSnackbar(`Test Email Sent!`, {
            variant: 'success'
          });
          actions.resetForm();
        } catch (error: any) {
          enqueueSnackbar(error?.Detail ?? 'Error sending test email, please try again.', {
            variant: 'error'
          });
        }
      }}
    >
      {({
        isSubmitting,
        values,
        initialValues,
        setFieldValue,
        handleSubmit,
        dirty,
        isValid,
        handleBlur,
        errors,
        touched
      }) => {
        return (
          <Form onSubmit={handleSubmit}>
            {isSubmitting && <Loader type="overlay" position="centered" title="Sending..." />}

            <Grid container spacing={1}>
              <Grid item xs={12} lg={8}>
                <Typography>
                  This form will send a test email. Please allow 5 minutes for the email to be
                  delivered. If the delivery fails, please contact IT to have them restart the email
                  service. It is running on SMBBlinq and is called BuilderLinqMailQueueService. If
                  the service is running and mail is still not being sent please have them review
                  the MailQueue table in the database for errors.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} lg={5}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <SMBSelect
                      variant="outlined"
                      required
                      onChange={e => {
                        setFieldValue('emailType', e.target.value);
                      }}
                      handleClear={() => {
                        setFieldValue('emailType', '');
                      }}
                      onBlur={handleBlur}
                      disabled={isLoadingEmailTypes}
                      name={`emailType`}
                      id={`emailType`}
                      label="Email Type"
                      value={values.emailType}
                      error={Boolean(touched.emailType && errors.emailType)}
                      helperText={touched.emailType && errors.emailType}
                    >
                      {emailTypes &&
                        emailTypes.map((type: IDropdownResponse, index) => {
                          return (
                            <MenuItem key={`${index}`} value={type.value}>
                              {type.description}
                            </MenuItem>
                          );
                        })}
                    </SMBSelect>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      onChange={e => {
                        setFieldValue('toAddress', e.target.value);
                      }}
                      disabled={isLoadingEmailTypes}
                      name={`toAddress`}
                      id={`toAddress`}
                      label="Mail To"
                      size="small"
                      value={values.toAddress}
                      onBlur={handleBlur}
                      error={Boolean(touched.toAddress && errors.toAddress)}
                      helperText={touched.toAddress && errors.toAddress}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Box mt={1}>
              <Button
                disabled={!dirty || isSubmitting || !isValid || isLoadingEmailTypes}
                type="submit"
                startIcon={<Email />}
                variant="contained"
                color="primary"
              >
                Send
              </Button>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};
